'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Sparkles, ArrowRight, Loader2 } from 'lucide-react';

const examplePrompts = [
  'Create a grand-opening campaign for my nail studio in Oslo',
  'Design a QR menu for my Italian restaurant with gold and black colors',
  'Make a birthday invitation for Emma\'s 30th birthday party',
  'Create an Instagram Reel cover for my fitness brand',
  'Design a complete brand identity for my coffee shop',
  'Generate a property listing for a luxury apartment in Bergen',
  'Create a TikTok promotion for my new clothing collection',
  'Make a Christmas campaign for my bakery with 5 variations',
];

export default function AIRequestBar() {
  const [inputValue, setInputValue] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isTyping) return;
    const currentPrompt = examplePrompts[promptIndex];
    if (charIndex < currentPrompt.length) {
      const timeout = setTimeout(() => {
        setPlaceholder(currentPrompt.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 40);
      return () => clearTimeout(timeout);
    } else {
      const pause = setTimeout(() => {
        setIsTyping(false);
        setTimeout(() => {
          setPlaceholder('');
          setCharIndex(0);
          setPromptIndex((i) => (i + 1) % examplePrompts.length);
          setIsTyping(true);
        }, 1800);
      }, 1200);
      return () => clearTimeout(pause);
    }
  }, [charIndex, isTyping, promptIndex]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    setIsLoading(true);
    // Backend integration: POST /api/ai/intake { prompt: inputValue }
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };

  return (
    <section className="py-10 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
          <Sparkles size={12} />
          AI Request Bar
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">
          Just Tell Us What You Need
        </h2>
        <p className="text-slate-300 mb-6 text-sm sm:text-base">
          Describe your project in plain language. Our AI team handles the rest.
        </p>

        <form onSubmit={handleSubmit} className="relative">
          <div className="relative rounded-2xl border border-border bg-card overflow-hidden gold-glow-sm focus-within:border-primary/50 transition-all duration-300">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={inputValue ? '' : (placeholder || 'Describe what you want to create...')}
              className="w-full bg-transparent px-4 sm:px-6 py-4 sm:py-5 pr-32 sm:pr-40 text-foreground text-sm sm:text-base placeholder-slate-400 focus:outline-none"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Link
                href="/sign-up-login-screen"
                onClick={handleSubmit as unknown as React.MouseEventHandler}
                className="btn-primary flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold"
              >
                {isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                <span className="hidden sm:inline">{isLoading ? 'Processing...' : 'Create'}</span>
                <span className="sm:hidden">Go</span>
                {!isLoading && <ArrowRight size={13} />}
              </Link>
            </div>
          </div>
        </form>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
          {examplePrompts.slice(0, 4).map((prompt, i) => (
            <button
              key={`prompt-chip-${i}`}
              onClick={() => setInputValue(prompt)}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-surface-3 border border-border text-slate-300 hover:text-foreground hover:border-primary/30 transition-all"
            >
              {prompt.slice(0, 38)}…
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}